import { useState, useEffect, useRef, isValidElement, type ReactNode } from "react";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "@/components/ui/dialog/Dialog";

interface TickerFeedProps {
  title: string;
  items: ReactNode[];
  scrollSpeed?: number;
}

export function TickerFeed({
  title,
  items,
  scrollSpeed = 0.5,
}: TickerFeedProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const tickerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const animationFrameRef = useRef<number | null>(null);
  const scrollPositionRef = useRef(0);

  useEffect(() => {
    if (!tickerRef.current || !contentRef.current) return;

    const animationStep = () => {
      if (!tickerRef.current || !contentRef.current) return;

      const effectiveSpeed = Math.max(0.1, scrollSpeed);

      const containerWidth = tickerRef.current.clientWidth;
      const singleSetWidth = contentRef.current.scrollWidth / 2;

      if (singleSetWidth === 0 || singleSetWidth <= containerWidth) {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        tickerRef.current.scrollLeft = 0;
        scrollPositionRef.current = 0;
        return;
      }

      tickerRef.current.scrollLeft += effectiveSpeed;

      if (tickerRef.current.scrollLeft >= singleSetWidth) {
        tickerRef.current.scrollLeft -= singleSetWidth;
      }

      scrollPositionRef.current = tickerRef.current.scrollLeft;

      animationFrameRef.current = requestAnimationFrame(animationStep);
    };

    const startAnimation = () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (tickerRef.current) {
        tickerRef.current.scrollLeft = scrollPositionRef.current;
      }
      animationFrameRef.current = requestAnimationFrame(animationStep);
    };

    const stopAnimation = () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };

    if (!isHovered) {
      const containerWidth = tickerRef.current.clientWidth;
      const singleSetWidth = contentRef.current.scrollWidth / 2;

      if (singleSetWidth > 0 && singleSetWidth > containerWidth) {
        startAnimation();
      } else {
        stopAnimation();
        tickerRef.current.scrollLeft = 0;
        scrollPositionRef.current = 0;
      }
    } else {
      stopAnimation();
    }

    return () => {
      stopAnimation();
    };
  }, [isHovered, items, scrollSpeed]);

  const handleMouseEnter = () => {
    if (tickerRef.current) {
      scrollPositionRef.current = tickerRef.current.scrollLeft;
    }
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    if (tickerRef.current) {
      tickerRef.current.scrollLeft = scrollPositionRef.current;
    }
    setIsHovered(false);
  };

  return (
    <div className="w-full">
      <div
        className="relative flex gap-2 items-center bg-slate-100 dark:bg-slate-800 text-white rounded-lg group cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-700 "
        onClick={() => setIsModalOpen(true)}
      >
        <div className="text-slate-400 px-4 p-3 flex-none font-mono text-xs uppercase tracking-widest">
          {title}
        </div>
        <div className="relative flex-grow overflow-hidden px-4">
          <div
            ref={tickerRef}
            className="w-full overflow-hidden whitespace-nowrap"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div ref={contentRef} className="inline-block">
              {items.map((item, index) => (
                <span
                  key={`item-${index}`}
                  className="inline-block align-middle"
                >
                  {isValidElement(item)
                    ? item
                    : typeof item === "object" && item !== null
                      ? (item as any)?.justification ||
                        (item as any)?.text ||
                        (item as any)?.description ||
                        String(item)
                      : item}
                  <span className="mx-4 text-slate-400 dark:text-slate-500 align-middle">
                    ·
                  </span>
                </span>
              ))}
              {items.map((item, index) => (
                <span
                  key={`item-${index}-duplicate`}
                  className="inline-block align-middle"
                >
                  {isValidElement(item)
                    ? item
                    : typeof item === "object" && item !== null
                      ? (item as any)?.justification ||
                        (item as any)?.text ||
                        (item as any)?.description ||
                        String(item)
                      : item}
                  {items.length > 0 && index < items.length - 1 && (
                    <span className="mx-4 text-slate-400 dark:text-slate-500 align-middle">
                      ·
                    </span>
                  )}
                </span>
              ))}
            </div>
          </div>
          {/* Left gradient overlay */}
          <div className="absolute left-0 top-0 bottom-0 w-8 pointer-events-none bg-gradient-to-r group-hover:from-slate-200 group-hover:dark:from-slate-700  to-transparent dark:from-slate-800" />
          {/* Right gradient overlay */}
          <div className="absolute right-0 top-0 bottom-0 w-8 pointer-events-none bg-gradient-to-l group-hover:from-slate-200 group-hover:dark:from-slate-700  to-transparent dark:from-slate-800" />
        </div>
      </div>

      <Dialog
        size="xl"
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      >
        <DialogTitle>{title}</DialogTitle>
        <DialogBody>
          <div className="space-y-4">
            {items.map((item, index) => (
              <div key={index} className=" ">
                {item}
              </div>
            ))}
          </div>
        </DialogBody>
        <DialogActions>
          <button
            onClick={() => setIsModalOpen(false)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Close
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
